import { z } from 'zod'

import {
  INVOICE_STATUSES,
  type InvoiceFilters,
  type InvoiceStatus,
} from '#admin/features/billing/api.ts'
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  type PaymentFilters,
  type PaymentMethod,
  type PaymentStatus,
} from '#admin/features/billing/payments-api.ts'

function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return values.find((candidate) => candidate === value)
}

function text(value: unknown): string | undefined {
  const q = typeof value === 'string' ? value.trim().slice(0, 200) : ''
  return q === '' ? undefined : q
}

/**
 * Filters live in the URL, so "overdue invoices" is a link somebody can send. Every key is
 * returned, `undefined` when dropped: the router spreads this over the raw search, so a key left
 * out would keep its raw value.
 */
export type InvoiceSearch = { q?: string | undefined; status?: InvoiceStatus | undefined }

export function parseInvoiceSearch(search: Record<string, unknown>): InvoiceSearch {
  return { q: text(search['q']), status: oneOf(INVOICE_STATUSES, search['status']) }
}

export function invoiceFiltersOf(search: InvoiceSearch): InvoiceFilters {
  return { q: search.q ?? '', status: search.status ?? '' }
}

export type PaymentSearch = {
  q?: string | undefined
  status?: PaymentStatus | undefined
  method?: PaymentMethod | undefined
  companyId?: string | undefined
  paidFrom?: string | undefined
  paidTo?: string | undefined
}

export function parsePaymentSearch(search: Record<string, unknown>): PaymentSearch {
  return {
    q: text(search['q']),
    status: oneOf(PAYMENT_STATUSES, search['status']),
    method: oneOf(PAYMENT_METHODS, search['method']),
    // The API refuses anything else with a 422, so a bad value is dropped here.
    companyId: z.uuid().safeParse(search['companyId']).data,
    paidFrom: z.iso.date().safeParse(search['paidFrom']).data,
    paidTo: z.iso.date().safeParse(search['paidTo']).data,
  }
}

export function paymentFiltersOf(search: PaymentSearch): PaymentFilters {
  return {
    q: search.q ?? '',
    status: search.status ?? '',
    method: search.method ?? '',
    companyId: search.companyId ?? '',
    paidFrom: search.paidFrom ?? '',
    paidTo: search.paidTo ?? '',
  }
}
